import priceFormatter from 'data/services/price/formatter';
import { SubscriptionTier } from 'types';
import formatTierRange from '../helpers/formatTierRange';
import {
    getTierAmountCents,
    getTierBandUsageDisplay,
} from '../helpers/tierUsage';
import {
    PriceTableContainer,
    Table,
    TableCell,
    TableHeader,
    TableRow,
    TableTotalRow,
} from './styled';

interface Props {
    tiers: SubscriptionTier[];
    currency: string;
    unit: string;
    totalUsage: number;
}

const getQuantityHeaderLabel = (unit: string): string => {
    if (!unit || unit === 'unit') {
        return 'Quantity';
    }
    return `Quantity (${unit})`;
};

const formatUsageUnit = (quantity: number, unit: string): string => {
    if (!unit || unit === 'unit') {
        return '';
    }

    const unitLabel = new Intl.PluralRules('en-US', { type: 'cardinal' }).select(quantity) === 'one'
        ? unit.slice(0, -1)
        : unit;

    return ` ${unitLabel}`;
};

const formatTotalQuantityLabel = (quantity: number, unit: string): string => (
    `${quantity.toLocaleString()}${formatUsageUnit(quantity, unit)}`
);

const formatUnitPrice = (tier: SubscriptionTier, currency: string): string => {
    if (tier.flat_amount_decimal || tier.flat_amount) {
        return '-';
    }

    const rawAmount = tier.unit_amount_decimal ?? tier.unit_amount;
    if (rawAmount == null) {
        return '-';
    }

    if (tier.unit_amount_decimal) {
        return priceFormatter(currency).format(parseFloat(tier.unit_amount_decimal) / 100);
    }

    return priceFormatter(currency).format(Number(tier.unit_amount) / 100);
};

const formatTierQuantity = (
    tier: SubscriptionTier,
    tierIndex: number,
    tiers: SubscriptionTier[],
    unit: string,
    totalUsage: number,
): string => {
    const quantity = getTierBandUsageDisplay(totalUsage, tierIndex, tiers, unit);
    return quantity.toLocaleString();
};

const formatTierAmount = (
    tier: SubscriptionTier,
    tierIndex: number,
    tiers: SubscriptionTier[],
    currency: string,
    unit: string,
    totalUsage: number,
): string => {
    const amountCents = getTierAmountCents(tier, tierIndex, tiers, unit, totalUsage);
    return priceFormatter(currency).format(amountCents / 100);
};

const PricingTiersTable = ({
    tiers,
    currency,
    unit,
    totalUsage,
}: Props) => {
    const totalQuantity = tiers.reduce(
        (sum, tier, index) => sum + getTierBandUsageDisplay(totalUsage, index, tiers, unit),
        0,
    );
    const totalAmountCents = tiers.reduce(
        (sum, tier, index) => sum + getTierAmountCents(tier, index, tiers, unit, totalUsage),
        0,
    );

    return (
        <PriceTableContainer>
            <Table>
                <thead>
                    <TableRow>
                        <TableHeader>Tier</TableHeader>
                        <TableHeader>{getQuantityHeaderLabel(unit)}</TableHeader>
                        <TableHeader>Unit Price</TableHeader>
                        <TableHeader>Amount</TableHeader>
                    </TableRow>
                </thead>
                <tbody>
                    {tiers.map((tier, index) => (
                        <TableRow
                            key={`tier-${tier.up_to ?? 'unlimited'}-${tier.unit_amount ?? tier.flat_amount ?? 'none'}`}
                        >
                            <TableCell>{formatTierRange(index, tiers, unit)}</TableCell>
                            <TableCell>
                                {formatTierQuantity(tier, index, tiers, unit, totalUsage)}
                            </TableCell>
                            <TableCell>{formatUnitPrice(tier, currency)}</TableCell>
                            <TableCell>
                                {formatTierAmount(
                                    tier,
                                    index,
                                    tiers,
                                    currency,
                                    unit,
                                    totalUsage,
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableTotalRow>
                        <TableCell>Total</TableCell>
                        <TableCell>
                            {formatTotalQuantityLabel(totalQuantity, unit)}
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>
                            {totalAmountCents > 0
                                ? priceFormatter(currency).format(totalAmountCents / 100)
                                : '-'}
                        </TableCell>
                    </TableTotalRow>
                </tbody>
            </Table>
        </PriceTableContainer>
    );
};

export default PricingTiersTable;
